import { typedSupabase } from '@/lib/typed-supabase';
import {
  IService,
  ServiceResponse,
} from '@/services/core/service-registry';
import { Profile } from "@/types/profile";
import { logger } from "@/lib/logger";
import { storageService } from "@/services/features/storage.service";
import { getErrorMessage } from "@/utils/serviceResponse";

// ============================================================================
// TYPES
// ============================================================================

export interface IProfileService extends IService {
  fetchUserProfile(userId: string): Promise<ServiceResponse<Profile>>;
  updateUserProfile(userId: string, profileData: Partial<Profile>): Promise<ServiceResponse<Profile>>;
  uploadProfileAvatar(userId: string, file: File): Promise<ServiceResponse<{ url: string }>>;
}

// ============================================================================
// PROFILE SERVICE - Phase 4 Architecture
// ============================================================================

/**
 * Profile Service for managing user profiles
 * Phase 4 Compliant - Implements IService with ServiceResponse wrappers
 */
export class ProfileService implements IProfileService {
  readonly name = "ProfileService";

  async initialize(): Promise<void> {
    logger.info("ProfileService initialized", { service: this.name });
  }

  async dispose(): Promise<void> {
    logger.info("ProfileService disposed", { service: this.name });
  }

  /**
   * Fetches the user profile by user ID
   */
  async fetchUserProfile(userId: string): Promise<ServiceResponse<Profile>> {
    try {
      const { data, error } = await typedSupabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        logger.error("Error fetching profile", { error: error.message, userId });
        return { success: false, error: error.message };
      }

      // Convert Json type to the expected structure in Profile type
      const profile: Profile = {
        ...data,
        social_links: data.social_links
          ? (data.social_links as Record<string, string>)
          : {},
      };

      return { success: true, data: profile };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to fetch profile";
      logger.error("Error fetching profile", { error: errorMessage, userId });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Updates the user profile
   */
  async updateUserProfile(
    userId: string,
    profileData: Partial<Profile>
  ): Promise<ServiceResponse<Profile>> {
    try {
      const { data, error } = await typedSupabase
        .from("profiles")
        .update(profileData)
        .eq("id", userId)
        .select()
        .single();

      if (error) {
        logger.error("Error updating profile", { error: error.message, userId });
        return { success: false, error: error.message };
      }

      // Convert Json type to the expected structure in Profile type
      const profile: Profile = {
        ...data,
        social_links: data.social_links
          ? (data.social_links as Record<string, string>)
          : {},
      };

      return { success: true, data: profile };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update profile";
      logger.error("Error updating profile", { error: errorMessage, userId });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Uploads a profile avatar image
   */
  async uploadProfileAvatar(
    userId: string,
    file: File
  ): Promise<ServiceResponse<{ url: string }>> {
    try {
      // Create a unique file path with user ID as folder for RLS policy
      const fileExt = file.name.split(".").pop();
      const fileName = `avatar-${Math.random()
        .toString(36)
        .substring(2)}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      // Upload the file using storage service
      const result = await storageService.uploadFile({
        bucket: 'avatars',
        path: filePath,
        file
      });

      if (!result.success) {
        return { success: false, error: result.error || "Upload failed" };
      }

      // Update the user's avatar_url in their profile
      const updateResult = await this.updateUserProfile(userId, {
        avatar_url: result.data.publicUrl,
      });

      if (!updateResult.success) {
        return { success: false, error: getErrorMessage(updateResult.error) };
      }

      return { success: true, data: { url: result.data.publicUrl } };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to upload avatar";
      logger.error("Error uploading avatar", { error: errorMessage, userId });
      return { success: false, error: errorMessage };
    }
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

export const profileServiceInstance = new ProfileService();

// ============================================================================
// LEGACY EXPORTS - For backward compatibility
// ============================================================================

/**
 * @deprecated Use profileServiceInstance.fetchUserProfile instead
 */
export const fetchUserProfile = async (userId: string): Promise<Profile | undefined> => {
  const response = await profileServiceInstance.fetchUserProfile(userId);
  return response.success ? response.data : undefined;
};

/**
 * @deprecated Use profileServiceInstance.updateUserProfile instead
 */
export const updateUserProfile = async (
  userId: string,
  profileData: Partial<Profile>
): Promise<{ success: boolean; data?: Profile; error?: string }> => {
  const response = await profileServiceInstance.updateUserProfile(userId, profileData);
  return response.success
    ? { success: true, data: response.data }
    : { success: false, error: getErrorMessage(response.error) };
};

/**
 * @deprecated Use profileServiceInstance.uploadProfileAvatar instead
 */
export const uploadProfileAvatar = async (
  userId: string,
  file: File
): Promise<{ success: boolean; url?: string; error?: string }> => {
  const response = await profileServiceInstance.uploadProfileAvatar(userId, file);
  return response.success
    ? { success: true, url: response.data.url }
    : { success: false, error: getErrorMessage(response.error) };
};
